import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
  Grid,
  Paper,
  Divider,
  IconButton,
  Collapse
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  BugReport as BugReportIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { JWTDebugger, JWTDebugResult } from '../utils/jwtDebugger';

interface JWTDebugPanelProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

export const JWTDebugPanel: React.FC<JWTDebugPanelProps> = ({ 
  isOpen = false, 
  onToggle 
}) => {
  const [debugResult, setDebugResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showTokenDetails, setShowTokenDetails] = useState(false);

  const runQuickDiagnostic = async () => {
    setLoading(true);
    try {
      const result = await JWTDebugger.quickDiagnostic();
      setDebugResult(result);
      console.log('JWT Debug Result:', result);
    } catch (error) {
      console.error('Debug error:', error);
      setDebugResult({
        tokenAnalysis: { isValid: false, isExpired: true, error: `${error}`, analysis: { hasProblematicChars: false } },
        recommendation: 'Failed to run diagnostic'
      });
    } finally {
      setLoading(false);
    }
  };

  const runTokenAnalysis = async () => {
    setLoading(true);
    try {
      const tokenAnalysis = await JWTDebugger.analyzeToken();
      setDebugResult({ tokenAnalysis, recommendation: 'Token analysis complete' });
    } catch (error) {
      console.error('Token analysis error:', error);
    } finally {
      setLoading(false);
    }
  };

  const testAPIEndpoint = async () => {
    setLoading(true);
    try {
      const apiTest = await JWTDebugger.testAPIEndpoint('/api/penholder/assignments/current');
      setDebugResult((prev: any) => ({ ...prev, apiTest }));
    } catch (error) {
      console.error('API test error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (result: any) => {
    if (!result) return 'default';
    if (result.tokenAnalysis?.error) return 'error';
    if (result.tokenAnalysis?.isExpired) return 'warning';
    if (result.tokenAnalysis?.isValid) return 'success';
    return 'default';
  };

  const getStatusText = (result: any) => {
    if (!result) return 'Not tested';
    if (result.tokenAnalysis?.error) return 'Error';
    if (result.tokenAnalysis?.isExpired) return 'Expired';
    if (result.tokenAnalysis?.isValid) return 'Valid';
    return 'Invalid';
  };

  return (
    <Card sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <BugReportIcon color="primary" />
            <Typography variant="h6">JWT Authentication Debug</Typography>
            {debugResult && (
              <Chip 
                label={getStatusText(debugResult)}
                color={getStatusColor(debugResult)}
                size="small"
              />
            )}
          </Box>
          {onToggle && (
            <IconButton onClick={onToggle} size="small">
              {isOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          )}
        </Box>

        <Collapse in={isOpen}>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Debug JWT token authentication issues. This panel helps diagnose API Gateway authorization problems.
            </Typography>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  onClick={runQuickDiagnostic}
                  disabled={loading}
                  fullWidth
                  startIcon={<BugReportIcon />}
                >
                  Quick Diagnostic
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  onClick={runTokenAnalysis}
                  disabled={loading}
                  fullWidth
                  startIcon={<RefreshIcon />}
                >
                  Analyze Token
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  onClick={testAPIEndpoint}
                  disabled={loading}
                  fullWidth
                >
                  Test API
                </Button>
              </Grid>
            </Grid>

            {debugResult && (
              <Box>
                <Alert 
                  severity={getStatusColor(debugResult) === 'error' ? 'error' : 
                           getStatusColor(debugResult) === 'warning' ? 'warning' : 'info'}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Recommendation
                  </Typography>
                  {debugResult.recommendation}
                </Alert>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">Token Analysis</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Token Status
                          </Typography>
                          <Box display="flex" flexDirection="column" gap={1}>
                            <Chip 
                              label={`Valid: ${debugResult.tokenAnalysis?.isValid ? 'Yes' : 'No'}`}
                              color={debugResult.tokenAnalysis?.isValid ? 'success' : 'error'}
                              size="small"
                            />
                            <Chip 
                              label={`Expired: ${debugResult.tokenAnalysis?.isExpired ? 'Yes' : 'No'}`}
                              color={debugResult.tokenAnalysis?.isExpired ? 'error' : 'success'}
                              size="small"
                            />
                            {debugResult.tokenAnalysis?.analysis?.timeUntilExpiry !== undefined && (
                              <Chip 
                                label={`Expires in: ${Math.floor(debugResult.tokenAnalysis.analysis.timeUntilExpiry / 60)}m`}
                                color="info"
                                size="small"
                              />
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Token Details
                          </Typography>
                          <Box display="flex" flexDirection="column" gap={1}>
                            {debugResult.tokenAnalysis?.analysis?.tokenLength && (
                              <Typography variant="body2">
                                Length: {debugResult.tokenAnalysis.analysis.tokenLength}
                              </Typography>
                            )}
                            {debugResult.tokenAnalysis?.analysis?.partsCount && (
                              <Typography variant="body2">
                                Parts: {debugResult.tokenAnalysis.analysis.partsCount}/3
                              </Typography>
                            )}
                            <Typography variant="body2">
                              Problematic chars: {debugResult.tokenAnalysis?.analysis?.hasProblematicChars ? 'Yes' : 'No'}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>

                    {debugResult.tokenAnalysis?.claims && (
                      <Box mt={2}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="subtitle2" gutterBottom>
                          JWT Claims
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2">
                                <strong>Audience:</strong> {debugResult.tokenAnalysis.claims.aud}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Subject:</strong> {debugResult.tokenAnalysis.claims.sub}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Token Use:</strong> {debugResult.tokenAnalysis.claims.token_use}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2">
                                <strong>Issuer:</strong> {debugResult.tokenAnalysis.claims.iss}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Issued:</strong> {new Date(debugResult.tokenAnalysis.claims.iat * 1000).toLocaleString()}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Expires:</strong> {new Date(debugResult.tokenAnalysis.claims.exp * 1000).toLocaleString()}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Box>
                    )}

                    {debugResult.tokenAnalysis?.error && (
                      <Box mt={2}>
                        <Alert severity="error">
                          <Typography variant="subtitle2">Error Details</Typography>
                          <Typography variant="body2">{debugResult.tokenAnalysis.error}</Typography>
                        </Alert>
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>

                {debugResult.apiTest && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle1">API Test Results</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Response Status
                            </Typography>
                            <Chip 
                              label={`${debugResult.apiTest.status} ${debugResult.apiTest.statusText}`}
                              color={debugResult.apiTest.status === 200 ? 'success' : 
                                     debugResult.apiTest.status === 401 ? 'warning' : 'error'}
                            />
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {debugResult.apiTest.analysis}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Response Body
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace', 
                                fontSize: '0.75rem',
                                maxHeight: 100,
                                overflow: 'auto',
                                bgcolor: 'grey.100',
                                p: 1,
                                borderRadius: 1
                              }}
                            >
                              {debugResult.apiTest.body}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};
